import { createContext, ReactNode, useContext, useMemo, useReducer, useState } from 'react';
import { AssessmentAnswer } from '../../../content/assessment/utils/assessmentUtils';
import { useFindCareUserQuery, useGetCareRecommendationStatusForUserQuery } from '../../../graphQL';
import { Setter } from '../../../types/reactTypes';
import { checkRequiredFields } from '../utils/checkRequiredFields';

type QuestionnaireKey = string;
type QuestionKey = string;

type Answers = Record<QuestionKey, AssessmentAnswer>;
type AnswersByQuestionnaire = Record<QuestionnaireKey, Answers>;
type AnswerAction = {
  answers: Answers;
  questionnaireKey?: string;
  type: 'set' | 'reset';
};

type FindCareContextType = {
  answersByQuestionnaire: AnswersByQuestionnaire;
  hasAllRequiredFields: boolean;
  hasStarted: boolean;
  isLoading: boolean;
  recommendationIsActive: boolean;
  resetAnswers: () => void;
  setAnswer: (questionnaireKey: string, questionKey: string, answer: AssessmentAnswer) => void;
  setHasStarted: Setter<boolean>;
};

const FindCareContext = createContext<FindCareContextType>({
  answersByQuestionnaire: {},
  hasAllRequiredFields: false,
  hasStarted: false,
  isLoading: true,
  recommendationIsActive: false,
  resetAnswers: () => undefined,
  setAnswer: () => undefined,
  setHasStarted: () => undefined,
});

export const FindCareConsumer = FindCareContext.Consumer;

export const FindCareProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [hasStarted, setHasStarted] = useState(false);
  const [hasAllRequiredFields, setHasAllRequiredFields] = useState(false);

  useFindCareUserQuery({
    onCompleted: patientData => {
      const hasRequired = checkRequiredFields(patientData.onboardedHubUser);
      setHasAllRequiredFields(hasRequired);
    },
  });

  const [answersByQuestionnaire, dispatch] = useReducer(
    (state: AnswersByQuestionnaire, action: AnswerAction) => {
      if (action.type === 'reset') {
        return {};
      }

      if (action.questionnaireKey === undefined) {
        return state;
      }

      const { questionnaireKey } = action;

      return {
        ...state,
        [questionnaireKey]: { ...(state[questionnaireKey] ?? {}), ...action.answers },
      };
    },
    {},
  );

  const { data: recData, loading: isLoading } = useGetCareRecommendationStatusForUserQuery();
  const recommendationIsActive = useMemo(
    () => recData?.getCareRecommendationStatusForUser?.activeRecommendation ?? false,
    [recData],
  );

  const setAnswer = (
    questionnaireKey: string,
    questionKey: string,
    answer: AssessmentAnswer,
  ): void => {
    dispatch({
      answers: {
        [questionKey]: answer,
      },
      questionnaireKey,
      type: 'set',
    });
  };

  const resetAnswers = (): void => {
    dispatch({ answers: {}, type: 'reset' });
  };

  const providerValue: FindCareContextType = {
    answersByQuestionnaire,
    hasAllRequiredFields,
    hasStarted,
    isLoading,
    recommendationIsActive,
    resetAnswers,
    setHasStarted,
    setAnswer,
  };

  return <FindCareContext.Provider value={providerValue}>{children}</FindCareContext.Provider>;
};

export const useFindCareContext = (): FindCareContextType => {
  return useContext(FindCareContext);
};
